// Google Tag Manager integration with IntersectionObserver for efficient event tracking

declare global {
  interface Window {
    dataLayer: any[];
    fbq?: any;
    saq?: any;
    gtag?: any;
  }
}

// Initialize dataLayer if it doesn't exist (queues all events until GTM loads)
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
}

// Throttle helper for high-frequency events (250ms minimum)
const throttle = (fn: Function, delay: number = 250) => {
  let lastCall = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
};

// IntersectionObserver setup for efficient viewport tracking
const createViewportObserver = (callback: (entry: IntersectionObserverEntry) => void, threshold = 0.5) => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }
  
  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback(entry);
        }
      });
    },
    {
      threshold,
      rootMargin: '0px'
    }
  );
};

// Track element when it enters viewport (fires once)
export const trackViewOnce = (element: Element, eventData: GTMEvent) => {
  if (!element) return;
  
  const observer = createViewportObserver((entry) => {
    gtmPushEvent(eventData);
    observer?.unobserve(entry.target);
  });
  
  observer?.observe(element);
};

export interface GTMEvent {
  event: string;
  [key: string]: any;
}

/**
 * Check if current environment is localhost only
 */
const isDevelopment = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost';
};

/**
 * Push an event to Google Tag Manager's dataLayer
 * Skips tracking in development/Lovable environments
 */
export const gtmPushEvent = (eventData: GTMEvent) => {
  if (isDevelopment()) {
    console.log('[DEV] Blocked GTM event:', eventData);
    return;
  }
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(eventData);
  }
};

/**
 * Track a page view in GTM
 */
export const gtmPageView = (pagePath: string, pageTitle?: string) => {
  gtmPushEvent({
    event: 'pageview',
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

/**
 * Track quiz events
 */
export const gtmQuizEvent = (
  action: 'started' | 'step_completed' | 'completed',
  data?: Record<string, any>
) => {
  gtmPushEvent({
    event: `quiz_${action}`,
    event_category: 'Quiz',
    event_action: action,
    ...data,
  });
};

/**
 * Track lead form events
 */
export const gtmLeadEvent = (
  action: 'viewed' | 'submitted' | 'validation_error',
  data?: Record<string, any>
) => {
  gtmPushEvent({
    event: `lead_form_${action}`,
    event_category: 'Lead Form',
    event_action: action,
    ...data,
  });
};

/**
 * Track results events
 */
export const gtmResultsEvent = (action: string, data?: Record<string, any>) => {
  gtmPushEvent({
    event: 'results_interaction',
    event_category: 'Results',
    event_action: action,
    ...data,
  });
};

/**
 * Track CTA clicks
 */
export const gtmCTAClick = (ctaName: string, location: string) => {
  gtmPushEvent({
    event: 'cta_click',
    event_category: 'CTA',
    event_action: 'click',
    cta_name: ctaName,
    cta_location: location,
  });
};

/**
 * Track navigation events
 */
export const gtmNavigationEvent = (
  action: 'external_link' | 'internal_link',
  data?: Record<string, any>
) => {
  gtmPushEvent({
    event: 'navigation',
    event_category: 'Navigation',
    event_action: action,
    ...data,
  });
};

/**
 * Track quiz step interactions (status, interests, priorities)
 */
export const gtmQuizStepEvent = (
  action: 'status_selected' | 'interest_toggled' | 'priorities_updated' | 'navigation',
  data?: Record<string, any>
) => {
  gtmPushEvent({
    event: `quiz_step_interaction`,
    event_category: 'Quiz',
    event_action: action,
    ...data,
  });
};

/**
 * Track form interactions with throttling for focus events
 */
export const gtmFormEvent = (
  action: 'field_focused' | 'field_completed' | 'validation_error' | 'consent_toggled' | 'submit_attempt',
  data?: Record<string, any>
) => {
  gtmPushEvent({
    event: `form_interaction`,
    event_category: 'Form',
    event_action: action,
    ...data,
  });
};

// Throttled version for high-frequency focus events
export const gtmFormEventThrottled = throttle(gtmFormEvent, 250);

/**
 * Track recruiter prompt interactions
 */
export const gtmRecruiterEvent = (
  action: 'viewed' | 'yes_clicked' | 'no_clicked',
  data?: Record<string, any>
) => {
  gtmPushEvent({
    event: `recruiter_prompt_${action}`,
    event_category: 'Recruiter Prompt',
    event_action: action,
    ...data,
  });
};

/**
 * Track role interactions
 */
export const gtmRoleEvent = (
  action: 'viewed' | 'clicked' | 'expanded',
  data?: Record<string, any>
) => {
  gtmPushEvent({
    event: 'role_interaction',
    event_category: 'Role',
    event_action: action,
    ...data,
  });
};

/**
 * Update consent status (Google Consent Mode v2)
 */
export const gtmUpdateConsent = (consent: {
  ad_storage?: 'granted' | 'denied';
  ad_user_data?: 'granted' | 'denied';
  ad_personalization?: 'granted' | 'denied';
  analytics_storage?: 'granted' | 'denied';
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', consent);
  }
};

/**
 * Track view_content event using IntersectionObserver
 * Call this with the hero/CTA element to track when it enters viewport
 */
export const trackContentView = (element: Element, contentData?: Record<string, any>) => {
  trackViewOnce(element, {
    event: 'view_content',
    event_category: 'Content',
    event_action: 'viewed',
    ...contentData
  });
};

/**
 * Track scroll depth in 25% increments (throttled to reduce CPU)
 */
export const trackScrollDepth = (() => {
  let maxDepth = 0;
  const depths = [25, 50, 75, 100];
  
  return throttle(() => {
    const scrollPercent = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    );
    
    depths.forEach(depth => {
      if (scrollPercent >= depth && depth > maxDepth) {
        maxDepth = depth;
        gtmPushEvent({
          event: 'scroll_depth',
          event_category: 'Engagement',
          event_action: 'scroll',
          scroll_depth: depth
        });
      }
    });
  }, 500);
})();
